import { PixelRatio } from 'react-native';

import { primitive } from 'styles/constants';
import { region } from 'utils/environment';
import { Region } from 'utils/environment/types';

import { RadialGradientConfig } from '../action-button-radial-gradient';
import { TakeoverMenuSectionConfig } from '../types';

import { ConfirmHeaderOverlay } from './confirm-header-overlay';
import { HeaderFlair } from './header-flair';

const DARK_BLUE = '#2676b2';
const MEDIUM_BLUE = '#7BEAFB';
const LIGHT_BLUE = '#E7F3F0';
const SAND = '#F3EFD9';
const GOLD = '#f2c337';
const GOLD_BORDER = '#c09a49';
const FONT_FAMILY = 'Tiki';
const PIXEL_RATIO = PixelRatio.get();

const COMMON_MENU_SECTION: TakeoverMenuSectionConfig = {
  headerBackgroundColor: MEDIUM_BLUE,
  itemBackgroundColor: LIGHT_BLUE,
  transitionPicture: require('./bubbles.webp'),
  transitionPictureAudio: require('./route-audio/bubbles.m4a'),
  transitionPictureTime: 2400,
  footerImage: require('./section-footer.webp'),
  footerImageOffset: 90,
};

const SPONGEBOB_MENU_SECTION_CONFIG: TakeoverMenuSectionConfig = {
  ...COMMON_MENU_SECTION,
  sectionBackgroundImage: require('./primary-section-bg.webp'),
  sectionImage: require('./dancing.webp'),
  transitionAudio: require('./route-audio/sb-menu-section_look-at-all-those-tasty-treasures.m4a'),
  sectionImageStyle: {
    width: 210,
    height: 324,
    position: 'absolute',
    right: -20,
    bottom: -60,
  },
  titleMaxWidth: 125,
};

const SPONGEBOB_KIDS_MENU_SECTION_CONFIG = {
  ...COMMON_MENU_SECTION,
  sectionBackgroundImage: require('./sand-section.webp'),
  sectionImage: require('./dancing-patrick.webp'),
  transitionAudio: require('./route-audio/sb-kids-menu-section_something-for-jr-jellyfishers.m4a'),
  sectionImageStyle: {
    width: 375 / PIXEL_RATIO,
    height: 558 / PIXEL_RATIO,
    position: 'absolute',
    right: 10,
    bottom: -40,
  },
  titleMaxWidth: 125,
};

const cartItemBackgroundImage = require('./cart-item-background.webp');

const BASE_ITEM_CONFIG = {
  cartItemImage: require('./pirate-bob.webp'),
  sectionBackgroundColor: LIGHT_BLUE,
  cartItemBackgroundImage,
};

const BIKINI_BOTTOM_BUNDLE_CONFIG = {
  ...BASE_ITEM_CONFIG,
  audio: require('./route-audio/pdp_jumpin-jellyfish-im-hungry.m4a'),
};

const KRABBY_WHOPPER_CONFIG = {
  ...BASE_ITEM_CONFIG,
  audio: require('./route-audio/pdp_the-krabby-whopper-great-choice.m4a'),
  additionalAudioSteps: {
    PickerCustomization: require('./route-audio/pdp_customization_so-many-delicious-toppings.m4a'),
    PickerCustomizationSides: require('./route-audio/pdp_sides_pick-your-favorite.m4a'),
    PickerCustomizationDrinks: require('./route-audio/pdp_drinks_use-your-imagination.m4a'),
  },
};

const KRABBY_WHOPPER_COMBO_CONFIG = {
  ...BASE_ITEM_CONFIG,
  audio: require('./route-audio/pdp_great-choice.m4a'),
};

const CHEESY_BACON_TOTS_CONFIG = {
  ...BASE_ITEM_CONFIG,
  audio: require('./route-audio/pdp_wowww-those-look-good.m4a'),
  cartItemImage: require('./krabs.webp'),
  cartItemBackgroundImage,
};

const STARBERRY_SHORTCAKE_PIE_CONFIG = {
  ...BASE_ITEM_CONFIG,
  audio: require('./route-audio/pdp_i-dont-need-it-i-dont-need-it.m4a'),
  cartItemImage: require('./patrick.webp'),
  cartItemBackgroundImage,
};

const PINEAPPLE_FLOAT_CONFIG = {
  ...BASE_ITEM_CONFIG,
  audio: require('./route-audio/pdp_the-pirates-frozen-pineapple-floats-my-boat.m4a'),
};

const F_U_N_BUNDLE_CONFIG = {
  ...BASE_ITEM_CONFIG,
  audio: require('./route-audio/pdp_fun_meal_use-your-imagination.m4a'),
};

const SWEET_CHOICE_AUDIO_CONFIG = {
  ...BASE_ITEM_CONFIG,
  audio: require('./route-audio/pdp_sweet-choice.m4a'),
};

const TASTY_TREASURE_AWAITS_AUDIO_CONFIG = {
  ...BASE_ITEM_CONFIG,
  audio: require('./route-audio/pdp_tasty-treasure-awaits.m4a'),
};

const TASTY_LOOT_AHEAD_AUDIO_CONFIG = {
  ...BASE_ITEM_CONFIG,
  audio: require('./route-audio/pdp_tasty-loot-ahead.m4a'),
};

const LETS_GET_GRILLIN_AUDIO_CONFIG = {
  ...BASE_ITEM_CONFIG,
  audio: require('./route-audio/pdp_lets-get-grillin.m4a'),
};

const LETS_SET_SAIL_TO_FLAME_GRILLIN_TOWN_AUDIO_CONFIG = {
  ...BASE_ITEM_CONFIG,
  audio: require('./route-audio/pdp_lets-set-sail-to-flame-grillin-town.m4a'),
};

const WOW_THOSE_LOOK_GOOD_AUDIO_CONFIG = {
  ...BASE_ITEM_CONFIG,
  audio: require('./route-audio/pdp_wowww-those-look-good-v2.m4a'),
};

export const spongebob = {
  splashScreen: {
    lottie: null as any, // not used for this one but maybe on next
    audio: null as any, // not used for this one but maybe on next
    video: require('./im-ready-splash.mp4'),
  },
  menuIcon: require('./menu-icon.webp'),
  fontFamily: FONT_FAMILY,
  primaryActionButton: {
    backgroundColor: GOLD,
    borderColor: GOLD_BORDER,
    borderWidth: 2,
    _text: {
      color: primitive.$bbqBrown,
      fontFamily: FONT_FAMILY,
    },
    _pressed: {
      backgroundColor: '#e3b330',
    },
  },
  primaryActionButtonRadialGradient: {
    center: { x: 0.5, y: 0.75 },
    radius: { x: 4, y: 4 },
    stops: [
      { color: '#FFF', offset: 0, opacity: 0.6 },
      { color: GOLD, offset: 0.2, opacity: 0 },
    ],
  } as RadialGradientConfig,
  homePage: {
    audio: require('./route-audio/home_are-you-ready-for-adventure.m4a'),
    targetSimpleHomePageIds: [
      // dev lunch simple home page
      '6cc4cd5a-9a89-49ca-a683-b5c741359b81',
      // staging... prod SHP?
      '55e96472-e610-4279-a2fa-646253da0982',
      // CA
      '0b0cb38a-c85c-4f21-95de-27b6ca9eabdc',
    ],
    containerBackgroundColor: DARK_BLUE,
    deliveryButton: {
      _text: {
        fontFamily: FONT_FAMILY,
        color: primitive.$white,
      },
      borderColor: primitive.$white,
    },
    muteButtonCoachmarkArgs:
      region === Region.US
        ? {
            // localization not required, this is US only.
            message: 'Tap the sound button to hear SpongeBob in the app! 🔊',
            imageSource: require('./pirate-bob.webp'),
          }
        : null,
  },
  menu: {
    audio: require('./route-audio/menu_aye-aye-captain-whatll-it-be.m4a'),
    targetSectionIds: {
      '8ac3193f-d088-42b5-b489-f497e19f2b9f': SPONGEBOB_MENU_SECTION_CONFIG,
      '4ce572da-7457-4509-a959-76a4664af3a2': SPONGEBOB_MENU_SECTION_CONFIG, // burgers for breakfast
      '2553668b-0bac-462b-b68e-74f3207addb1': SPONGEBOB_MENU_SECTION_CONFIG, // CA

      // kids menu section
      section_5603: SPONGEBOB_KIDS_MENU_SECTION_CONFIG,
      'e16a5fec-06a5-4346-89f1-0ca2dc07b203': SPONGEBOB_KIDS_MENU_SECTION_CONFIG, // CA

      // DEV TESTING ONLY! 1M AND legacy - lunch meals
      // '463b62de-01b7-4af7-b5ea-6d6337dfb142': SPONGEBOB_MENU_SECTION_CONFIG,
    },
  },

  pdp: {
    targetMenuItemIds: {
      // krabby whopper variants
      '6d04d6d4-ad81-42e6-9589-183c2dfd81bb': KRABBY_WHOPPER_CONFIG,
      '60274ec8-0ad0-4f5e-ab12-5e55560ac1dd': KRABBY_WHOPPER_CONFIG,
      'b2f53699-fb2a-49d2-ab32-0a79931a826c': KRABBY_WHOPPER_CONFIG,
      item_92764: KRABBY_WHOPPER_CONFIG, // reward
      // CA
      'cd7a78f8-8750-4786-98e6-5adb1027d45b': KRABBY_WHOPPER_CONFIG,
      '4d75f386-ae1a-409e-bec2-2ba36bc18365': KRABBY_WHOPPER_CONFIG,

      // krabs cheesy bacon tots
      '091a414b-62e6-4b06-a111-ca5daa22191e': CHEESY_BACON_TOTS_CONFIG,
      item_92772: CHEESY_BACON_TOTS_CONFIG, // reward
      item_92773: CHEESY_BACON_TOTS_CONFIG, // reward
      // CA krabs cheesy coins
      '49819d58-a56b-4cc1-9b22-18d9039ca58c': CHEESY_BACON_TOTS_CONFIG,

      item_95197: BASE_ITEM_CONFIG, // CA - Gary's Strawberry Swirl Sundae
      item_95337: BASE_ITEM_CONFIG, // CA - Pirate's Sparkling Pineapple Float

      // patricks starberry shortcake pie
      item_92771: STARBERRY_SHORTCAKE_PIE_CONFIG,

      // pirate froz pineapple float / fanta one
      'cd5f8d24-dc6a-40c2-9325-ce8a7d0ca3c7': PINEAPPLE_FLOAT_CONFIG,
      '7aec7c8c-2559-4c76-a577-3059f7d21c02': PINEAPPLE_FLOAT_CONFIG,
      item_97169: PINEAPPLE_FLOAT_CONFIG, // reward
      item_96629: PINEAPPLE_FLOAT_CONFIG, // reward
      item_97170: PINEAPPLE_FLOAT_CONFIG, // reward

      // bikini bottom bundle
      '9db369b4-62c3-4399-8e53-d99e3d1bc3c2': BIKINI_BOTTOM_BUNDLE_CONFIG,
      '9d07eba5-b697-45cf-b8c6-01b69fe29f1d': BIKINI_BOTTOM_BUNDLE_CONFIG, // CA

      // F.U.N bundle
      '608da2a4-8e1d-4db7-9914-514c633ddb0f': F_U_N_BUNDLE_CONFIG,
      'a44a0da4-c831-44ea-a0d7-dccfd516cda0': F_U_N_BUNDLE_CONFIG,

      // krabby whopper combo
      '4b7a328a-cae1-46f4-b56a-082aacabba56': KRABBY_WHOPPER_COMBO_CONFIG,
      '2fb9e6d5-3b2f-47af-83f5-153f6505508b': KRABBY_WHOPPER_COMBO_CONFIG,
      '81ea77d0-f851-4363-b92c-a402ad9d6b03': TASTY_LOOT_AHEAD_AUDIO_CONFIG,

      // other bundles / combos
      'db72356f-fa5a-406f-93e3-1a89a5d2c0df': LETS_SET_SAIL_TO_FLAME_GRILLIN_TOWN_AUDIO_CONFIG,
      '6416c23e-e729-497d-a57f-247f965c22a1': LETS_SET_SAIL_TO_FLAME_GRILLIN_TOWN_AUDIO_CONFIG,
      'faaf75ac-114c-4c17-8cee-de7265c5be56': SWEET_CHOICE_AUDIO_CONFIG,
      '0ae1ac83-dc52-40a9-9e9b-878021d61260': BASE_ITEM_CONFIG,
      'dc4acbba-071b-4bbf-8f0a-1328cdb88609': BASE_ITEM_CONFIG,
      '7eb5a22d-9a9c-42f6-b3c2-64d45c0394dc': BASE_ITEM_CONFIG,
      '1ddbca4d-e357-4185-8c26-24426fcb52c3': BASE_ITEM_CONFIG,
      '5e3b54df-054c-4d13-8506-0f87e5d7a4e6': BASE_ITEM_CONFIG,
      '26faf835-3327-45b2-8924-dcfafa84795b': BASE_ITEM_CONFIG,
      '54fb961a-909d-43a5-8739-6f607abe49ee': BASE_ITEM_CONFIG,

      // Other CA stuff
      'beceeb00-8c99-42bb-a039-40ee92392a05': BASE_ITEM_CONFIG,
      '909c1fae-8d77-403a-9e5e-5c23f3715f4f': BASE_ITEM_CONFIG,
      '381f1583-5318-4915-9042-0dcc76ab3dd6': BASE_ITEM_CONFIG,
      'd9c76db9-b44b-4235-95d7-c73590937ee9': BASE_ITEM_CONFIG,
      'f17760cf-4ca2-4055-9a7b-e5f647a5dbe1': BASE_ITEM_CONFIG,

      // kids meals
      combo_5796: LETS_GET_GRILLIN_AUDIO_CONFIG,
      'fa2b8189-37e4-4924-824a-536781e28a3c': BASE_ITEM_CONFIG,
      '94d04292-af9f-42b1-ad9c-f14ad7b82c0a': BASE_ITEM_CONFIG,
      '8ca04d01-53ad-472e-8665-de77b77c7987': BASE_ITEM_CONFIG,
      combo_5797: WOW_THOSE_LOOK_GOOD_AUDIO_CONFIG,
      combo_5798: WOW_THOSE_LOOK_GOOD_AUDIO_CONFIG,
      combo_5774: TASTY_TREASURE_AWAITS_AUDIO_CONFIG,

      // PAR nuggets kids meals
      'ac9ef440-6f5f-4469-b725-016f4912e303': WOW_THOSE_LOOK_GOOD_AUDIO_CONFIG,
      'dcf15858-82c9-4f10-bae6-b4f0d7c80033': WOW_THOSE_LOOK_GOOD_AUDIO_CONFIG,

      // NCR kids meals
      'afdd6640-7af3-4dc3-a373-bb73905a4982': LETS_GET_GRILLIN_AUDIO_CONFIG, // cheeseburger
      '789f1206-d335-42b6-afba-c1b6743a1457': WOW_THOSE_LOOK_GOOD_AUDIO_CONFIG, // 4 pc nuggets
      'b87ca460-a231-4fda-8067-01e27e58269f': WOW_THOSE_LOOK_GOOD_AUDIO_CONFIG,
      '258be439-eb43-4c31-91a9-96ad243b38df': TASTY_TREASURE_AWAITS_AUDIO_CONFIG,
    },
    headerBackgroundColor: MEDIUM_BLUE,
    backgroundColor: SAND,
    backgroundImage: require('./pdp-sand-bg.webp'),
    HeaderFlair,
    overviewTopImage: require('./pdp-overview-ocean.webp'),
    overviewBottomImage: require('./pdp-overview-sand.webp'),
    overviewBottomImageOffset: 100,
  },
  cart: {
    audio: require('./route-audio/cart_lets-keep-moving-swashbuckler.m4a'),
    paymentProcessingVideo: require('./checkout.mp4'),
    paymentProcessingBackgroundColor: '#20618f',
  },
  orderConfirmDetails: {
    headerBackgroundColor: '#20618d',
    headerVideo: require('./order-confirm-details.mp4'),
    ConfirmHeaderOverlay,
    headerVideoAspectRatio: 2.539,
    headerFontColor: primitive.$white,
  },
};
